import React, { useCallback, useEffect, useRef, useState } from "react";
import { formwizApi } from "./formwizApi";
import { eventBus } from "../../utils/eventBus";
import { practiceFeatures } from "../../config/practice";
import { useSendFormsWizard } from "./launch";
import { useStaffFormFill } from "../internal/launch";
import { internalApi } from "../internal/internalApi";
import { FormsLanguageChip } from "../internal/PatientLanguage";
import SubmissionReview from "./SubmissionReview";
import RequestActions from "./RequestActions";
import {
  CHANNEL_SHORT,
  EMR_STATUS,
  emrStatusOf,
  REQUEST_STATUS,
  SUBMISSION_STATUS,
  errInfo,
  fmtDateTime,
  fmtScore,
  patientDisplayName,
  sevTone,
  statusInfo,
} from "./staffUtils";
import "./staff.css";

function SubmissionRow({ sub, onOpen }) {
  const [label, tone] = statusInfo(SUBMISSION_STATUS, sub.status);
  const submitted = sub.status === "submitted";
  const content = (
    <>
      <span className="fws-sub-title">{sub.form_title || sub.form_id}</span>
      <span className={`fws-chip fws-tone-${tone}`}>{label}</span>
      {submitted && sub.primary_score !== null && sub.primary_score !== undefined && (
        <span className="fws-chip fws-tone-muted">
          {fmtScore(sub.primary_score)}{sub.primary_band ? ` · ${sub.primary_band}` : ""}
        </span>
      )}
      {submitted && sub.flag_severity && sub.flag_severity !== "none" && (
        <span className={`fws-chip ${sub.flag_severity === "critical" ? "is-critical" : `fws-tone-${sevTone(sub.flag_severity)}`}`}>
          {sub.flag_severity === "critical" ? "Critical flag" : "Flag"}
        </span>
      )}
      {submitted && (
        <span className={`fws-chip fws-tone-${sub.review_status === "reviewed" ? "ok" : "warn"}`}>
          {sub.review_status === "reviewed" ? "Reviewed" : "Needs review"}
        </span>
      )}
      {submitted && sub.emr_status && sub.emr_status !== "none" && (
        <span className={`fws-chip fws-tone-${statusInfo(EMR_STATUS, emrStatusOf(sub))[1]}`}>{statusInfo(EMR_STATUS, emrStatusOf(sub))[0]}</span>
      )}
    </>
  );
  return submitted ? (
    <button type="button" className="fws-sub" onClick={() => onOpen(sub.id)} aria-label={`Review ${sub.form_title || sub.form_id}`}>
      {content}
    </button>
  ) : (
    <div className="fws-sub">{content}</div>
  );
}

// [label, tone] chips for one staff form (InternalSummary).
export function staffFormChips(it) {
  const out = [];
  if (!it) return out;
  if (it.status === "in_progress") return [["Draft", "info"]];
  if (it.kind === "amendment") out.push(["Amendment", "warn"]);
  if (it.emr_status === "posted") out.push(["In EMR", "ok"]);
  else if (it.emr_status === "failed") out.push(["EMR failed", "bad"]);
  else if (it.emr_status === "unknown" || it.emr_uncertain) out.push(["EMR unconfirmed", "warn"]);
  else if (it.emr_status === "queued" || it.emr_status === "posting") out.push(["EMR queued", "info"]);
  else if (it.revision) out.push([`Saved rev ${it.revision}`, "ok"]);
  if (it.amended_by_submission_id) out.push(["Amended", "muted"]);
  if (it.has_pending_edit) out.push(["Unsaved edit", "warn"]);
  return out;
}

function StaffFormRow({ item, onOpen }) {
  const who = item.finalized_by || item.created_by;
  const when = item.edited_at || item.submitted_at || item.created_at;
  return (
    <button type="button" className="fws-sub" onClick={() => onOpen(item)} aria-label={`Open ${item.form_title || item.form_id}`} data-testid={`fwi-staff-row-${item.id}`}>
      <span className="fws-sub-title">{item.form_title || item.form_id}</span>
      {staffFormChips(item).map(([l, tone]) => <span key={l} className={`fws-chip fws-tone-${tone}`}>{l}</span>)}
      <span className="fws-muted fws-small">{who ? `by ${who}` : ""}{when ? ` · ${fmtDateTime(when)}` : ""}</span>
    </button>
  );
}

/*
 * Chart Forms tab: this patient's Form Wizard requests (newest first) with
 * their submissions, plus "Send forms". Hidden quietly when the server says
 * 404 (not deployed / practice not enabled).
 *   data  the chart row (patientid, appointmentid, firstname, lastname, dob)
 */
export default function PatientFormsPanel({ data, dob }) {
  const patientid = data && data.patientid;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState("");
  const [reviewId, setReviewId] = useState(null);
  const [openWizard, wizard] = useSendFormsWizard();
  const [openStaffForm, staffFormFill] = useStaffFormFill();
  // Internal staff forms (practiceFeatures().staffForms): null until known,
  // false when the server has none (404 / 503: the section stays hidden).
  const staffForms = !!practiceFeatures().staffForms;
  const [staffAvail, setStaffAvail] = useState(null);
  const [staffItems, setStaffItems] = useState([]);
  const [shown, setShown] = useState(false);
  const patientName = patientDisplayName(data);

  const staffOff = useRef(false);
  const loadStaff = useCallback(async () => {
    if (!patientid || !staffForms || staffOff.current) return;
    try {
      const res = await internalApi.patientList(patientid);
      setStaffItems(Array.isArray(res && res.items) ? res.items : []);
      setStaffAvail(true);
    } catch (e) {
      const st = errInfo(e).status;
      staffOff.current = st === 404 || st === 503;
      setStaffAvail(!staffOff.current);
    }
  }, [patientid, staffForms]);

  const load = useCallback(async () => {
    if (!patientid) return;
    setError("");
    try {
      const res = await formwizApi.patientForms(patientid);
      setRequests(Array.isArray(res && res.requests) ? res.requests : []);
      setShown(true);
    } catch (e) {
      const info = errInfo(e);
      if (info.status === 404) setHidden(true);
      else setError(info.status === 403 ? "You don't have access to this patient's forms." : "Couldn't load the forms. Try again.");
    } finally {
      setLoading(false);
    }
  }, [patientid]);

  useEffect(() => {
    load();
  }, [load]);

  // The staff-forms list once the panel is up (never when formwiz is absent).
  useEffect(() => {
    if (shown) loadStaff();
  }, [shown, loadStaff]);
  useEffect(() => {
    const h = (e) => {
      if (!e || !e.patientid || String(e.patientid) === String(patientid)) loadStaff();
    };
    eventBus.on("formwiz:staff-forms-changed", h);
    return () => eventBus.off("formwiz:staff-forms-changed", h);
  }, [loadStaff, patientid]);

  useEffect(() => {
    const onKioskClosed = () => load();
    const onChanged = (e) => {
      if (!e || !e.patientid || String(e.patientid) === String(patientid)) load();
    };
    eventBus.on("formwiz:kiosk-closed", onKioskClosed);
    eventBus.on("formwiz:requests-changed", onChanged);
    return () => {
      eventBus.off("formwiz:kiosk-closed", onKioskClosed);
      eventBus.off("formwiz:requests-changed", onChanged);
    };
  }, [load, patientid]);

  if (!patientid || hidden) return null;
  // Internal staff-form containers are listed under "Staff forms", not as
  // requests sent to the patient.
  const sent = requests.filter((r) => r.channel !== "internal");
  const openStaffItem = (it) => {
    if (it.status === "in_progress" || it.has_pending_edit) {
      openStaffForm(data, { submissionId: it.id, dob, onDone: () => { load(); loadStaff(); } });
    } else setReviewId(it.id);
  };

  return (
    <section className="fws fws-panel" aria-label="Form Wizard" data-testid="patient-forms-panel">
      <div className="fws-panel-head">
        <h3>Forms sent to the patient</h3>
        <span className="fws-chip fws-tone-muted">{sent.length}</span>
        {shown && <FormsLanguageChip patientid={patientid} />}
        <span className="fws-spacer" />
        <button type="button" className="fws-btn fws-btn-sm" onClick={() => { load(); loadStaff(); }} disabled={loading}>Refresh</button>
        {staffForms && staffAvail === true && (
          <button type="button" className="fws-btn fws-btn-sm" onClick={() => openStaffForm(data, { dob, onDone: () => { load(); loadStaff(); } })}>
            Fill staff form
          </button>
        )}
        <button type="button" className="fws-btn fws-btn-accent fws-btn-sm" onClick={() => openWizard(data, { dob })}>
          Send forms
        </button>
      </div>
      {error && <div className="fws-alert fws-alert-error" style={{ marginTop: 10 }}>{error}</div>}
      {loading && requests.length === 0 && <p className="fws-muted fws-small" style={{ marginTop: 8 }}>Loading…</p>}
      {!loading && !error && sent.length === 0 && (
        <p className="fws-muted fws-small" style={{ marginTop: 8 }}>No forms sent yet. Use “Send forms” to text, email or hand a tablet to the patient.</p>
      )}
      {sent.map((r) => {
        const [label, tone] = statusInfo(REQUEST_STATUS, r.status);
        const subs = Array.isArray(r.submissions) ? r.submissions : [];
        const done = subs.filter((s) => s.status === "submitted").length;
        return (
          <div key={r.id} className="fws-req" data-testid={`fws-request-${r.id}`}>
            <div className="fws-req-head">
              <span className={`fws-chip fws-tone-${tone}`}>{label}</span>
              <span className="fws-strong">{subs.length || (r.forms || []).length} form{(subs.length || (r.forms || []).length) === 1 ? "" : "s"}</span>
              <span className="fws-muted fws-small">{done}/{subs.length || (r.forms || []).length} done</span>
              <span className="fws-chip fws-tone-muted">{CHANNEL_SHORT[r.channel] || r.channel}{r.destination_masked ? ` · ${r.destination_masked}` : ""}</span>
              <span className="fws-chip fws-tone-muted">{r.language === "es" ? "ES" : "EN"}</span>
            </div>
            <div className="fws-req-meta">
              {r.created_by ? `By ${r.created_by} · ` : ""}{fmtDateTime(r.last_sent_at || r.created_at)}
              {r.status === "completed" && r.completed_at ? ` · completed ${fmtDateTime(r.completed_at)}` : r.expires_at ? ` · expires ${fmtDateTime(r.expires_at)}` : ""}
              {r.send_error ? ` · ${r.send_error}` : ""}
            </div>
            {r.note && <div className="fws-req-meta">Note: {r.note}</div>}
            {subs.length > 0 && (
              <div className="fws-subs">
                {subs.map((s) => <SubmissionRow key={s.id} sub={s} onOpen={setReviewId} />)}
              </div>
            )}
            {/* Its changes emit formwiz:requests-changed, which re-reads this list. */}
            <RequestActions request={{ ...r, patientid: r.patientid || patientid }} patientName={patientName} />
          </div>
        );
      })}
      {staffForms && staffAvail === true && (
        <div className="fwi-staff-list" data-testid="fwi-staff-forms">
          <h4>Staff forms</h4>
          {staffItems.length === 0 ? (
            <p className="fws-muted fws-small">No staff forms yet. Use “Fill staff form” for forms staff complete themselves (not sent to the patient).</p>
          ) : (
            <div className="fws-subs">
              {staffItems.map((it) => <StaffFormRow key={it.id} item={it} onOpen={openStaffItem} />)}
            </div>
          )}
        </div>
      )}
      {reviewId && (
        <SubmissionReview submissionId={reviewId} onClose={() => { setReviewId(null); loadStaff(); }} onChanged={() => { load(); loadStaff(); }} />
      )}
      {wizard}
      {staffFormFill}
    </section>
  );
}
